// frontend/app/exhibit/v2/screens/AttractScreen.js

import React, { useEffect, useState } from "react";
import { Image, Pressable, StyleSheet, View } from "react-native";
import { LocalizedText as Text } from "../i18n/ExhibitLocaleContext";

import { getExhibitAttemptStats } from "../../api/aiExhibitAnalyticsClient";
import { aiExplainedAssetUrl, getRealOrAIPhotoRound } from "../../api/aiExhibitContentClient";
import { tx } from "../i18n/exhibitLocale";

function PhotoCard({ side, round, onPress, language = "en" }) {
  const data = round?.[String(side).toLowerCase()] || {};
  return (
    <Pressable
      onPress={() => onPress(side, round)}
      accessibilityRole="button"
      accessibilityLabel={data.label || `${tx(language, "Photo")} ${side}`}
      style={({ pressed }) => [styles.photo, pressed && styles.photoPressed]}
    >
      <Image
        source={{ uri: aiExplainedAssetUrl(data.assetPath) }}
        resizeMode="cover"
        style={styles.photoImage}
        accessibilityLabel={data.alt || data.label || `${tx(language, "Photo")} ${side}`}
      />
      <View style={styles.photoLabel}>
        <Text style={styles.photoLabelText}>{side}</Text>
      </View>
    </Pressable>
  );
}

export default function AttractScreen({ language = "en", onChoose = () => {}, onSkip = () => {} }) {
  const [round, setRound] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getRealOrAIPhotoRound()
      .then(async (item) => {
        if (!active) return;
        setRound(item);
        try {
          const live = await getExhibitAttemptStats({
            stationId: "real-or-ai",
            itemId: item.id,
            contentVersion: language === "es" ? `${item.contentVersion}_es` : item.contentVersion,
          });
          if (active) setStats(live);
        } catch (statsError) {
          console.warn("[AI EXPLAINED V2] Attract stats unavailable:", statsError?.message || statsError);
        }
      })
      .catch((contentError) => {
        if (active) setError(contentError?.message || "Photo content is temporarily unavailable.");
      });
    return () => { active = false; };
  }, [language]);

  const attempts = Number(stats?.scored_attempts || 0);
  const enoughStats = attempts >= 5;

  return (
    <View style={styles.container}>
      <View style={styles.topBar}><Text style={styles.brand}>{tx(language, "AI · Without the Hype or Fear")}</Text></View>
      <View style={styles.content}>
        <Text style={styles.title}><Text style={styles.accent}>{tx(language, "Real")}</Text>{` ${tx(language, "or")} `}<Text style={styles.accent}>{tx(language, "AI")}</Text>{"?"}</Text>

        {error ? (
          <>
            <Text style={styles.error}>{tx(language, "Photo round is temporarily unavailable.")}</Text>
            <Pressable onPress={onSkip} style={styles.skip}><Text style={styles.skipText}>{tx(language, "continue to the menu →")}</Text></Pressable>
          </>
        ) : !round ? (
          <Text style={styles.loading}>{tx(language, "Loading today's photo challenge…")}</Text>
        ) : (
          <>
            <Text style={styles.subtitle}>{tx(language, round.title?.hs || "One was photographed. One was generated with AI.")}</Text>
            <Text style={styles.instruction}>{tx(language, round.sub?.hs || "Touch your guess.")}</Text>

            <View style={styles.pair}>
              <PhotoCard side="A" round={round} onPress={onChoose} language={language} />
              <PhotoCard side="B" round={round} onPress={onChoose} language={language} />
            </View>

            {enoughStats ? (
              <Text style={styles.tally}><Text style={styles.tallyNumber}>{stats.pct_incorrect}%</Text>{language === "es" ? ` de ${attempts} intentos registrados eligieron la opción incorrecta` : ` of ${attempts} recorded attempts picked wrong`}</Text>
            ) : (
              <Text style={styles.tally}>
                <Text style={styles.tallyNumber}>{attempts}</Text>
                {language === "es"
                  ? attempts === 1 ? " intento registrado hasta ahora" : " intentos registrados hasta ahora"
                  : attempts === 1 ? " live attempt recorded so far" : " live attempts recorded so far"}
              </Text>
            )}

            <Text style={styles.tiny}>{tx(language, "Live Saras attempt data · no seeded visitor percentages")}</Text>
            <Pressable onPress={onSkip} style={styles.skip}><Text style={styles.skipText}>{tx(language, "or skip to the menu →")}</Text></Pressable>
          </>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { width: 1920, height: 1080, backgroundColor: "#070D17" },
  topBar: { height: 64, paddingHorizontal: 56, justifyContent: "center" },
  brand: { fontSize: 19, fontWeight: "800", color: "#8FA5BC", letterSpacing: 0.8 },
  content: { alignItems: "center", paddingHorizontal: 120 },
  title: { marginTop: 30, fontSize: 96, lineHeight: 102, fontWeight: "900", color: "#F4F8FC", letterSpacing: -2 },
  accent: { color: "#4FD1FF" },
  subtitle: { marginTop: 8, fontSize: 32, fontWeight: "700", color: "#C7D5E3", textAlign: "center" },
  instruction: { marginTop: 7, fontSize: 23, color: "#8FA5BC" },
  pair: { flexDirection: "row", gap: 44, marginTop: 30 },
  photo: { width: 640, height: 400, borderRadius: 28, overflow: "hidden", position: "relative", backgroundColor: "#152238", shadowColor: "#000000", shadowOpacity: 0.55, shadowRadius: 40, shadowOffset: { width: 0, height: 22 } },
  photoPressed: { opacity: 0.92 },
  photoImage: { width: "100%", height: "100%" },
  photoLabel: { position: "absolute", left: 0, right: 0, bottom: 0, height: 66, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(4,10,18,0.56)" },
  photoLabelText: { fontSize: 30, fontWeight: "900", color: "#FFFFFF" },
  tally: { marginTop: 26, fontSize: 25, fontWeight: "700", color: "#F4F8FC" },
  tallyNumber: { fontSize: 31, color: "#4FD1FF", fontWeight: "900" },
  tiny: { marginTop: 8, fontSize: 15, color: "#5F7690" },
  skip: { marginTop: 20, paddingVertical: 10, paddingHorizontal: 18 },
  skipText: { fontSize: 22, fontWeight: "700", color: "#8FA5BC" },
  loading: { marginTop: 170, fontSize: 30, color: "#A9BCCE" },
  error: { marginTop: 170, fontSize: 30, color: "#FBBF24", fontWeight: "800" },
});
